import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { Fragment, memo, useState, useCallback } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import FormGenerality from './form';
import {
  NewPersonVM,
  ParentVM,
  SearchPersonVM,
} from 'shell/components/shared/sipo-person-form/new-person';
import SearchPerson from '../shared/search-person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { ResponseAnprData } from 'shell/models/person';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

const MaternityComponent = (): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const formContext = useFormikContext<NewPersonVM>();

  const [, getPerson] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    AccordionGeneralityForm | undefined
  >(`${__PERSON_NEW_API_URL__}/persona`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => {
      const subjects = response.data.payload?.risposta3002OK?.listaSoggetti.datiSoggetto || [];

      const [subject] = subjects;

      const generalityAnpr = transformGenerality(subject || {}) || undefined;

      return generalityAnpr;
    },
  });

  const onSearchSearchPerson = useEventCallback(() => {
    const data = {
      criteriRicerca: {
        codiceFiscale: formContext.values.maternity?.searchPerson?.fiscalCode || undefined,
        nome: formContext.values.maternity?.searchPerson?.name || undefined,
        cognome: formContext.values.maternity?.searchPerson?.surname || undefined,
        senzaCognome:
          formContext.values.maternity?.searchPerson?.withoutSurname ||
          !formContext.values.maternity?.searchPerson?.surname
            ? '1'
            : undefined,
        senzaNome:
          formContext.values.maternity?.searchPerson?.withoutName ||
          !formContext.values.maternity?.searchPerson?.name
            ? '1'
            : undefined,
      },
      datiRichiesta: {
        ricercaNazionale: '1',
      },
    };

    (
      getPerson({ data }) as ResponsePromise<
        HttpResponse<ResponseAnprData>,
        AccordionGeneralityForm | undefined
      >
    ).then((response) => {
      const responseCasted = response as AccordionGeneralityForm | undefined;

      formContext.setFieldValue('maternity', {
        searchPerson: undefined,
        generality: {
          ...(responseCasted || {}),
        },
        isFromSearchPerson: true,
      });
    });
  });

  const onChangeSearchPerson = useEventCallback(
    (key: keyof SearchPersonVM, value: SearchPersonVM[keyof SearchPersonVM]) => {
      formContext.setFieldValue('maternity', {
        ...(formContext.values.maternity || {}),
        searchPerson: {
          ...(formContext.values.maternity?.searchPerson || {}),
          [key]: value,
        },
      });
    }
  );

  const onFieldGeneralityChange = useEventCallback(
    (
      key: keyof AccordionGeneralityForm,
      value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
    ) => {
      formContext.setFieldValue('maternity', {
        ...(formContext.values.maternity || {}),
        generality: {
          ...(formContext.values.maternity?.generality || {}),
          [key]: value,
        },
      });
    }
  );

  const onFieldsGeneralityChange = useEventCallback((values: AccordionGeneralityForm) => {
    formContext.setFieldValue('maternity', {
      ...(formContext.values.maternity || {}),
      generality: {
        ...(formContext.values.maternity?.generality || {}),
        ...values,
      },
    });
  });

  const resetForm = useCallback(() => {
    formContext.setValues({
      ...formContext.values,
      maternity: {
        generality: {
          birthDate: null,
          fiscalCode: '',
          foreign: undefined,
          gender: '',
          isForeign: false,
          italian: undefined,
          name: '',
          surname: '',
        },
      },
    });
  }, [formContext.values, formContext.setValues]);

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-maternity-content"
          id="panel-maternity-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Maternità</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SearchPerson
            values={formContext.values.maternity?.searchPerson}
            onChange={onChangeSearchPerson}
            onSearch={onSearchSearchPerson}
            resetForm={resetForm}
          />
          <FormGenerality
            onChangeGenerality={onFieldGeneralityChange}
            onChangeFieldsGenerality={onFieldsGeneralityChange}
            generality={formContext.values.maternity?.generality || undefined}
            errorsGenerality={
              (formContext.errors.maternity as FormikErrors<ParentVM>)
                ?.generality as FormikErrors<AccordionGeneralityForm>
            }
            touchedGenerality={
              (formContext.touched.maternity as unknown as FormikTouched<ParentVM>)
                ?.generality as unknown as FormikTouched<AccordionGeneralityForm>
            }
            labelMunicipalityItalian="Comune nascita"
            labelProvinceItalian="Provincia nascita"
            disabled={formContext.values.maternity?.isFromSearchPerson || false}
          />
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

MaternityComponent.displayName = 'Accordion Maternity component';

const Maternity = memo(MaternityComponent);

export default Maternity;
